import React from 'react';
import { MinusCircleIcon } from '@heroicons/react/24/outline';
import ChatAvatar from './ChatAvatar';
import type { ChatRoomMember } from '../../types/chat';

interface MemberListProps {
  members: ChatRoomMember[];
  selfId: string | null;
  canManage?: boolean;
  onRemove?: (userId: string) => void;
}

/**
 * Per-room member list. Matches Figma `4029:196974` ListItem spec:
 * 40 px avatar with online dot, two-line text (name 14 px text-primary,
 * role 14 px text-secondary), 8 px vertical padding, 16 px gap.
 *
 * Admin-only remove button only appears on row hover.
 */
const MemberList: React.FC<MemberListProps> = ({ members, selfId, canManage, onRemove }) => {
  const selfUpper = (selfId ?? '').toUpperCase();
  return (
    <ul className="flex flex-col">
      {members.map((m) => {
        const isSelf = (m.user_id ?? '').toUpperCase() === selfUpper;
        const subtitle = m.role_name ?? (m.is_admin ? 'Admin' : '');
        return (
          <li
            key={m.user_id}
            className="group flex items-center gap-4 px-2 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800/40"
          >
            <ChatAvatar
              src={m.avatar_url}
              name={m.full_name ?? m.email}
              size={40}
              online={m.is_online}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-text-primary truncate">
                {m.full_name ?? m.email}
                {isSelf && (
                  <span className="ml-1 text-[11px] text-text-tertiary">(you)</span>
                )}
              </p>
              {subtitle && (
                <p className="text-sm text-text-secondary truncate">{subtitle}</p>
              )}
            </div>
            {canManage && !isSelf && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(m.user_id)}
                aria-label={`Remove ${m.full_name ?? m.email}`}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full text-text-tertiary hover:text-error-600 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <MinusCircleIcon className="w-4 h-4" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default MemberList;
